import {Button, Input, Modal, Tooltip} from 'antd';
import {ReactElement, useMemo, useState} from 'react';

import {useLocale} from '../../../../../../../../provider/locale/locale-hook';
import {LocalePlural} from '../../../../../../../../provider/locale/locale-plural';
import {Locale} from '../../../../../../../../provider/locale/localization';
import {useModal} from '../../../../../../../../provider/modal/modal-hook';
import {useSnackbar} from '../../../../../../../../provider/snackbar/snackbar-hook';
import {fetchReviewsReply} from '../../../../../../../../service/reviews/reviews-reply';
import {ReviewDataType} from '../../../../../../../../service/reviews/reviews-type';
import {isReviewReplyAllowed} from '../../../../../reviews-helper';
import {ReviewDataWithCatalogType} from '../../../review-list-type';
import {processBatch} from '../../review-helper';

const {TextArea} = Input;

type PropsType = {
    onCancel: () => void;
    onSuccess?: () => void;
    refreshReview: (reviewId: number) => Promise<unknown>;
    updateReview: (reviewId: number, updateData: Partial<ReviewDataType>) => void;
    selectedReviews: Array<ReviewDataWithCatalogType>;
};

export function BulkAnswerModal(props: PropsType): ReactElement {
    const {onCancel, onSuccess, selectedReviews, updateReview, refreshReview} = props;
    const [answerText, setAnswerText] = useState<string>('');
    const [isLoading, setIsLoading] = useState(false);
    const {snackbar} = useSnackbar();
    const {getLocalizedString} = useLocale();
    const {modal} = useModal();

    const answerableReviews = useMemo(
        () => selectedReviews.filter((review) => isReviewReplyAllowed(review)),
        [selectedReviews]
    );

    const canAnswerCount = answerableReviews.length;
    const totalCount = selectedReviews.length;
    const canAnswerAll = canAnswerCount === totalCount;
    const canAnswerNone = canAnswerCount === 0;

    async function submitAnswers() {
        setIsLoading(true);

        try {
            // Process answers in batches
            await processBatch(answerableReviews, 50, async (review: ReviewDataWithCatalogType) => {
                await fetchReviewsReply(review.id, {
                    comment: answerText.trim(),
                });
                updateReview(review.id, {});
            });

            // Refresh all reviews to show updated data in batches
            await processBatch(answerableReviews, 50, async (review: ReviewDataWithCatalogType) => {
                await refreshReview(review.id);
            });

            snackbar.success(<Locale stringKey="REVIEWS__REVIEW_ANSWER_SEND_SUCCESS__TITLE" />);

            if (onSuccess) {
                onSuccess();
            } else {
                onCancel();
            }
        } catch {
            snackbar.error(<Locale stringKey="ERROR__SOMETHING_WENT_WRONG" />);
        } finally {
            setIsLoading(false);
        }
    }

    async function handleOk() {
        if (!answerText.trim()) {
            return;
        }

        if (!canAnswerAll) {
            modal.confirm({
                maskClosable: true,
                title: <Locale stringKey="REVIEWS__BULK_ANSWER__CONFIRM_TITLE" />,
                content: (
                    <div>
                        <LocalePlural
                            count={totalCount}
                            fewKey="REVIEWS__BULK_ANSWER__CONFIRM_CONTENT__FEW"
                            manyKey="REVIEWS__BULK_ANSWER__CONFIRM_CONTENT__MANY"
                            singularKey="REVIEWS__BULK_ANSWER__CONFIRM_CONTENT__SINGULAR"
                            valueMap={{
                                canAnswerCount: String(canAnswerCount),
                                totalCount: String(totalCount),
                            }}
                        />
                    </div>
                ),
                okText: <Locale stringKey="TEXT__APPLY" />,
                cancelText: <Locale stringKey="POPUP__BUTTON__CANCEL" />,
                onOk: async () => {
                    await submitAnswers();
                },
            });

            return;
        }

        await submitAnswers();
    }

    return (
        <Modal
            footer={[
                <Button key="cancel" onClick={onCancel}>
                    <Locale stringKey="POPUP__BUTTON__CANCEL" />
                </Button>,
                <Tooltip
                    key="tooltip"
                    title={canAnswerNone ? <Locale stringKey="REVIEWS__BULK_ANSWER__DISABLED_TOOLTIP" /> : null}
                >
                    <Button
                        disabled={!answerText.trim() || canAnswerNone}
                        key="ok"
                        loading={isLoading}
                        onClick={handleOk}
                        type="primary"
                    >
                        <Locale stringKey="TEXT__APPLY" />
                    </Button>
                </Tooltip>,
            ]}
            onCancel={onCancel}
            open
            title={<Locale stringKey="REVIEWS__REPLY" />}
        >
            <TextArea
                // eslint-disable-next-line id-length, unicorn/prevent-abbreviations
                onChange={(event) => setAnswerText(event.target.value)}
                placeholder={getLocalizedString('REVIEWS__ENTER_YOUR_REPLY')}
                rows={4}
                value={answerText}
            />
        </Modal>
    );
}
